import { Injectable } from '@angular/core';
import { CognitoErrors, ForgotPasswordFlows, UserGroups } from '@thx/enums';
import { AuthProvider, IAuthServerResponse, IUser } from '@thx/interfaces';
import {
  AuthSession,
  AuthUser,
  confirmResetPassword,
  confirmSignIn,
  confirmSignUp,
  fetchAuthSession,
  getCurrentUser,
  resendSignUpCode,
  type ResendSignUpCodeOutput,
  resetPassword,
  signIn,
  signInWithRedirect,
  signOut,
  signUp,
  type SignUpOutput,
  updatePassword,
} from 'aws-amplify/auth';
import { Bind } from 'lodash-decorators';
import { BehaviorSubject, catchError, defer, from, iif, map, Observable, of, tap, zip } from 'rxjs';
import { concatMap, mergeMap } from 'rxjs/operators';
import { UserConverter } from '../converters';
import { AuthLoginError } from '../errors';

@Injectable({ providedIn: 'root' })
export class AuthService {
  currentUserSession$ = new BehaviorSubject<AuthSession | null>(null);
  currentUser$ = new BehaviorSubject<IUser | null>(null);
  currentCognitoUser$ = new BehaviorSubject<AuthUser | null>(null);

  private _userConverter: UserConverter = new UserConverter();

  signUp(
    username: string,
    autoSignInEnabled: boolean = false,
    userPassword?: string,
    userAttributes?: { [key: string]: string },
    clientMetadata?: { [key: string]: string },
  ): Observable<IAuthServerResponse<IUser>> {
    const password = userPassword ? userPassword : this._generatePassword();
    const input = { username, password };
    const options = {};

    if (userAttributes) {
      Object.assign(options, { userAttributes });
    }

    if (clientMetadata) {
      Object.assign(options, { clientMetadata });
    }

    if (autoSignInEnabled) {
      Object.assign(options, { autoSignIn: true });
    }

    if (Object.keys(options).length > 0) {
      Object.assign(input, { options });
    }
    return from(signUp({ ...input })).pipe(
      map((result: SignUpOutput) => ('error' in result ? result : { data: result, loading: true })),
      concatMap((result: IAuthServerResponse<SignUpOutput>) => {
        if (result.error) {
          return of(result);
        } else {
          const email = username;
          const sub = result.data?.userId;

          // sejurexotic signUp process has an extra step CONFIRM_SIGN_UP and cannot reset password for an Unconfirmed email address
          if (result.data?.nextStep?.signUpStep === ForgotPasswordFlows.CONFIRM_SIGN_UP) {
            return of({
              data: { email: email, sub: sub } as IUser,
              loading: false,
            } as IAuthServerResponse<IUser>);
          } else {
            // for b2b signups proceed to reset password
            return this.resetPasswordAtSignUp({ email, sub } as IUser);
          }
        }
      }),
      catchError((error: any): Observable<IAuthServerResponse> => of({ error, loading: false })),
    );
  }

  resendSignUp(email: string): Observable<IAuthServerResponse<ResendSignUpCodeOutput>> {
    return from(resendSignUpCode({ username: email })).pipe(
      catchError((error: any) => of({ error: error })),
      map((response: any) => {
        return response.error ? { error: response.error } : { data: response };
      }),
    );
  }

  resetPasswordAtSignUp(emailOrUser: string | IUser): Observable<IAuthServerResponse> {
    const email = typeof emailOrUser === 'string' ? emailOrUser : emailOrUser.email;
    if (!email) {
      return of({ error: new Error('Email not provided for the forget password operation.') });
    }
    return this._forgotPassword(email, ForgotPasswordFlows.SIGN_UP).pipe(
      map((result: IAuthServerResponse) => {
        if (!result.error || result.error?.code === CognitoErrors.CODE_DELIVERY_FAILURE) {
          result.data = emailOrUser;
          if (result.error?.code === CognitoErrors.CODE_DELIVERY_FAILURE) {
            result.error = CognitoErrors.CODE_DELIVERY_FAILURE;
          }
        }
        return result;
      }),
      catchError((error) => of(error)),
    );
  }

  userResetPassword(
    emailOrUser: string | IUser,
    flow?: ForgotPasswordFlows,
    clientMetadata?: { [key: string]: string },
  ): Observable<IAuthServerResponse> {
    const email = typeof emailOrUser === 'string' ? emailOrUser : emailOrUser.email;
    if (!email) {
      return of({ error: new Error('Email not provided for the forget password operation.') });
    }
    return this._forgotPassword(email, flow || ForgotPasswordFlows.USER_RESET, clientMetadata);
  }

  userChangePassword(user: AuthUser, oldPassword: string, newPassword: string): Observable<IAuthServerResponse> {
    return from(updatePassword({ oldPassword, newPassword })).pipe(
      catchError((error: any) => of({ error: error })),
      map((response: any) => {
        return response?.error ? { error: response.error } : { data: 'Password succesfuly sent!' };
      }),
    );
  }

  forgotPasswordSubmit(
    username: string,
    confirmationCode: string,
    newPassword: string,
  ): Observable<IAuthServerResponse<IUser>> {
    return from(confirmResetPassword({ username, newPassword, confirmationCode })).pipe(
      concatMap(() => this.signIn(username, newPassword)),
    );
  }

  forgotPasswordSubmitWithoutSingIn(username: string, confirmationCode: string, newPassword: string): Observable<void> {
    return from(confirmResetPassword({ username, newPassword, confirmationCode }));
  }

  confirmSignUp(username: string, confirmationCode: string, email?: string): Observable<IAuthServerResponse<IUser>> {
    return from(confirmSignUp({ username, confirmationCode })).pipe(
      map((result) => {
        if (result && !result.isSignUpComplete) {
          return { data: undefined, error: 'Could not complete sign up' };
        }
        return { data: { username, email } } as IAuthServerResponse<IUser>;
      }),
      catchError((error: any): Observable<IAuthServerResponse> => of({ error, loading: false })),
    );
  }

  signIn(username: string, password: string): Observable<IAuthServerResponse<IUser>> {
    return from(signIn({ username, password })).pipe(
      mergeMap((result) =>
        iif(
          () => !result.isSignedIn,
          defer(() => of({ error: 'Could not sign in user.' }) as Observable<IAuthServerResponse<IUser>>),
          defer(() => this._onSuccessAuthenticate()),
        ),
      ),
    );
  }

  socialSignIn(provider: AuthProvider): Observable<void> {
    return from(
      signInWithRedirect({
        provider,
      }),
    );
  }

  signOut(error?: AuthLoginError): Observable<IAuthServerResponse> {
    return from(signOut()).pipe(
      catchError((error: any): Observable<IAuthServerResponse> => of({ error, loading: false })),
      tap(() => {
        this.currentUserSession$.next(null);
        this.currentUser$.next(null);
      }),
      map(() => ({ loading: false, error })),
    );
  }

  completeNewPassword(user: AuthUser, password: string): Observable<IAuthServerResponse<IUser>> {
    return from(signIn({ username: user.username, password })).pipe(
      concatMap((result) =>
        from(
          confirmSignIn({
            challengeResponse: 'newPassword',
            options: {
              userAttributes: {
                sub: user.username,
              },
            },
          }),
        ),
      ),
      mergeMap((result) =>
        iif(
          () => result.isSignedIn,
          defer(() => this._onSuccessAuthenticate()),
          defer(() => of({ error: 'Could not set the new password.' }) as Observable<IAuthServerResponse<IUser>>),
        ),
      ),
    );
  }

  currentUser(): Observable<IUser> {
    return from(zip(fetchAuthSession(), getCurrentUser())).pipe(
      map((results) => this._setUser(results[0], results[1])),
    );
  }

  currentUserSession(): Observable<AuthSession | null> {
    return from(fetchAuthSession()).pipe(
      map((session: AuthSession) => this._setUserSession(session)),
      catchError(() => of(null)),
    );
  }

  setCurrentAuthenticatedUser(): Observable<IAuthServerResponse<IUser>> {
    return from(zip(fetchAuthSession(), getCurrentUser())).pipe(
      map((results) => {
        const user = this._setUser(results[0], results[1]);
        this._setUserSession(results[0]);
        return { data: user, loading: false };
      }),
      catchError(() => of({ error: 'User session is not set.' })),
    );
  }

  isCurrentUserLoggedIn(): boolean {
    const session = this.currentUserSession$.getValue();
    return !!session?.tokens?.accessToken;
  }

  isCurrentUserLoggedIn$(): Observable<boolean> {
    return from(fetchAuthSession()).pipe(map((session) => !!session?.tokens?.accessToken));
  }

  isUserLoggedOut(): boolean {
    return !this.isCurrentUserLoggedIn();
  }

  isUserAdmin(user: IUser | null): boolean {
    if (user) {
      return user.groups?.includes(UserGroups.ADMINS) === true;
    }
    return false;
  }

  isCurrentUserAdmin(): boolean {
    return this.isUserAdmin(this.currentUser$.getValue());
  }

  isUserAgent(user: IUser | null): boolean {
    if (user) {
      return user.groups?.includes(UserGroups.AGENTS) === true;
    }
    return false;
  }

  isCurrentUserAgent(): boolean {
    return this.isUserAgent(this.currentUser$.getValue());
  }

  isUserAccountManager(user: IUser | null): boolean {
    if (user) {
      return user.groups?.includes(UserGroups.ACCOUNT_MANAGERS) === true;
    }
    return false;
  }

  isCurrentUserAccountManager(): boolean {
    return this.isUserAccountManager(this.currentUser$.getValue());
  }

  isUserOrderAdmin(user: IUser | null): boolean {
    if (user) {
      return user.groups?.includes(UserGroups.ORDER_ADMINS) === true;
    }
    return false;
  }

  isCurrentUserOrderAdmin(): boolean {
    return this.isUserOrderAdmin(this.currentUser$.getValue());
  }

  isUserMarketingManager(user: IUser | null): boolean {
    if (user) {
      return user.groups?.includes(UserGroups.MARKETING_MANAGERS) === true;
    }
    return false;
  }

  isCurrentUserMarketingManager(): boolean {
    return this.isUserMarketingManager(this.currentUser$.getValue());
  }

  private _forgotPassword(
    username: string,
    flow: ForgotPasswordFlows,
    metadata?: { [key: string]: string },
  ): Observable<IAuthServerResponse> {
    const clientMetadata = { flow };
    if (metadata) {
      Object.assign(clientMetadata, metadata);
    }
    return from(resetPassword({ username, options: { clientMetadata } })).pipe(
      catchError((error: any): Observable<IAuthServerResponse> => of({ error, loading: false })),
      map((result: any) => (result.error ? result : { data: username, loading: false })),
    );
  }

  @Bind
  private _onSuccessAuthenticate(): Observable<IAuthServerResponse<IUser>> {
    return from(fetchAuthSession({ forceRefresh: true })).pipe(
      map((result) => {
        this._setUserSession(result);
        return { data: result, loading: false } as IAuthServerResponse<AuthSession>;
      }),
      concatMap((session) => from(getCurrentUser())),
      map((user) => {
        const session = this.currentUserSession$.getValue();
        if (session) {
          const convertedUser = this._userConverter.convertUser(
            this.currentUserSession$.getValue() as AuthSession,
            user,
          );
          this._setUser(this.currentUserSession$.getValue() as AuthSession, user);
          return { data: convertedUser, loading: false } as IAuthServerResponse<IUser>;
        }
        return { error: 'Could not retrieve user session.', loading: false } as IAuthServerResponse<IUser>;
      }),
    );
  }

  private _setUser(userSession: AuthSession, authUser: AuthUser): IUser {
    const user = this._userConverter.convertUser(userSession, authUser);
    this.currentUser$.next(user);
    this.currentCognitoUser$.next(authUser);
    return user;
  }

  private _setUserSession(session: AuthSession): AuthSession | null {
    this.currentUserSession$.next(session);
    return session;
  }

  private _generatePassword(): string {
    const len = 40;
    const p = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789*#@&()';
    return [...Array(len)].reduce((a) => a + p[~~(Math.random() * p.length)], '');
  }
}
